import React, { useState, useCallback, useRef, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router-dom'
import {
  Download,
  ArrowRight,
  RotateCcw,
  Video,
  FolderOpen,
} from 'lucide-react'
import { ReactComponent as StyleShotIcon } from 'svg/style-shot-icon.svg'
import { ReactComponent as NextCameraIcon } from 'svg/next-camera-icon.svg'
import JSZip from 'jszip'

// 단계별 컴포넌트들
import { StepIndicator } from './components/StepIndicator'
import { ModelSelectionStep } from './components/steps/ModelSelectionStep'
import { StyleConfirmStep } from './components/steps/StyleConfirmStep'
import { StyleDescriptionStep } from './components/steps/StyleDescriptionStep'
import { CompositionResultStep } from './components/steps/CompositionResultStep'
import { PhotoSetStep } from './components/steps/PhotoSetStep'
import { AvatarType, ThemeType, aiStudioService } from 'service/aiStudio'
import { ThemeSelectionStep } from './components/steps/ThemeSelectionStep'
import { StyleClipPromoModal } from './components/StyleClipPromoModal'
import { PointInsufficientModal } from './components/PointInsufficientModal'
import { ClothingData } from 'pages/ai-studio/tabs/FashionEditorTab/type/type'
import { useGetSynthesisRequestStatus } from 'hooks/query/aiStudio/useGetSynthesisRequestStatus'
import useGetVideoQuota from 'hooks/query/aiStudio/useGetVideoQuota'
import { usePointManager } from 'hooks/usePointManager'
import useUserStore from 'store/useUserStore'
import { FashionStudioHero } from 'pages/ai-studio/components/FashionStudioHero'
import { NewStyleDescriptionStep } from './components/steps/NewStyleDescriptionStep'

// 타입 정의

export const FashionEditorTab: React.FC = () => {
  const navigate = useNavigate()
  const { userInfo } = useUserStore()
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarType | null>(null)
  const [selectedTheme, setSelectedTheme] = useState<ThemeType[]>([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [clothingData, setClothingDataState] = useState<ClothingData>({
    category: 'top',
    topImage: null,
    bottomImage: null,
    topImageId: null,
    bottomImageId: null,
    topDescription: '',
    bottomDescription: '',
    combinedImage: [],
    generatedImages: [],
    garmentRequestId: 0,
    selectedGarmentJobId: 0,
    isUseSample: false,
    samples: [],
    sampleRequestStatus: '',
    requestStatus: '',
    aspectRatio: '9:16',
    topParsedGarmentInfo: null,
    bottomParsedGarmentInfo: null,
  })

  // PhotoSet 관련 상태
  const [isPhotoSetGenerating, setIsPhotoSetGenerating] = useState(false)
  const [photoSet, setPhotoSet] = useState<
    {
      jobId: number
      backgroundName: string
      poseName: string
      status: string
      resultUrl: string
    }[]
  >([])
  const [processingPhotoCount, setProcessingPhotoCount] = useState(0)
  const [selectedPhotoSetImage, setSelectedPhotoSetImage] = useState<
    string | null
  >(null)
  const [currentPhotoSetJobId, setCurrentPhotoSetJobId] = useState<
    number | null
  >(null)
  const photoSetPollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const divRef = useRef<HTMLDivElement | null>(null)

  // 비디오 요청 상태 플래그
  const [hasVideoRequested, setHasVideoRequested] = useState(false)

  // 스타일 클립 프로모 모달 상태
  const [isStyleClipPromoModalOpen, setIsStyleClipPromoModalOpen] =
    useState(false)

  // 포인트 부족 모달 상태
  const [isPointInsufficientModalOpen, setIsPointInsufficientModalOpen] =
    useState(false)
  const [heroHeight, setHeroHeight] = useState(500)
  // 다운로드 진행률 상태
  const [downloadProgress, setDownloadProgress] = useState({
    isDownloading: false,
    current: 0,
    total: 0,
    failed: 0,
  })

  //requestId 데이터 요청
  const { data } = useGetSynthesisRequestStatus(currentPhotoSetJobId, 1500)

  // 비디오 할당량 조회
  const videoQuotaQuery = useGetVideoQuota()

  // 포인트 관리 (스타일 샷 세트 생성 - 900 캐시 필요)
  const { currentPoint, hasEnoughPoints, checkPointsRealtime } =
    usePointManager({
      requiredPoints: 900,
    })

  const validateCategory = () => {
    const category = clothingData.category
    const topId = clothingData.topImageId
    const bottomId = clothingData.bottomImageId
    const isUseSample = clothingData.isUseSample
    if (category === 'top') {
      // top일때 topId가있고 (bottomId가있거나 isUseSample이 true)
      return topId && (bottomId || isUseSample)
    } else if (category === 'bottom') {
      // bottom일때 bottomId가있고 (topId가있거나 isUseSample이 true)
      return bottomId && (topId || isUseSample)
    } else if (category === 'dress') {
      // dress일때 topId만 있으면 됌
      return !!topId
    }

    return false
  }

  const setClothingData = useCallback(
    (data: ClothingData | ((prev: ClothingData) => ClothingData)) => {
      if (typeof data === 'function') {
        setClothingDataState((prev) => data(prev))
      } else {
        setClothingDataState(data)
      }
    },
    [],
  )

  // 실시간 포인트 체크 후 PhotoSet 생성 함수
  const handleGeneratePhotoSetWithPointCheck = useCallback(async () => {
    // 실시간 포인트 체크
    const hasEnoughPoints = await checkPointsRealtime()

    if (!hasEnoughPoints) {
      // 포인트 부족 시 모달 표시
      setIsPointInsufficientModalOpen(true)
      return
    }

    // 포인트 충분하면 PhotoSet 생성 진행
    handleGeneratePhotoSet()
  }, [checkPointsRealtime])

  // PhotoSet 생성 함수 (새로운 synthesis-requests API 사용)
  const handleGeneratePhotoSet = useCallback(async () => {
    console.log('📸 PhotoSet generation started')
    console.log(clothingData, 'photoSet Check data')

    if (
      !selectedAvatar ||
      clothingData.selectedGarmentJobId === 0 ||
      clothingData.garmentRequestId === 0 ||
      clothingData.combinedImage.length === 0
    ) {
      console.log('❌ PhotoSet generation cancelled - missing requirements')
      return
    }

    // 기존 PhotoSet 관련 상태 초기화
    setPhotoSet([])
    setProcessingPhotoCount(0)
    setSelectedPhotoSetImage(null)
    setCurrentPhotoSetJobId(null)
    setHasVideoRequested(false) // 비디오 요청 플래그 초기화

    setIsPhotoSetGenerating(true)

    try {
      // 업로드된 이미지 ID 배열 생성 (clothingData에서 추출)
      const uploadedImageIds: { imageId: number; garmentType: string }[] = []
      if (clothingData.topImageId) {
        uploadedImageIds.push({
          imageId: clothingData.topImageId,
          garmentType: 'top',
        })
      }
      if (clothingData.bottomImageId) {
        uploadedImageIds.push({
          imageId: clothingData.bottomImageId,
          garmentType: 'bottom',
        })
      }

      if (uploadedImageIds.length === 0) {
        console.error('No uploaded image IDs found for PhotoSet generation')
        setIsPhotoSetGenerating(false)
        return
      }

      // 옷 타입 결정
      const clothesType = clothingData.category || 'top'

      // 옷 설명 생성
      const clothesDescriptions: { top?: string; bottom?: string } = {}
      if (clothingData.topDescription) {
        clothesDescriptions.top = clothingData.topDescription
      }
      if (clothingData.bottomDescription) {
        clothesDescriptions.bottom = clothingData.bottomDescription
      }

      const synthesisResponse =
        await aiStudioService.garmentSynthesisRequestsBySelectGarmentId(
          clothingData.garmentRequestId,
          clothingData.selectedGarmentJobId,
        )

      console.log('📨 PhotoSet synthesis response:', synthesisResponse)

      // APIResponse 구조에서 데이터 추출
      let responseData
      if ('data' in synthesisResponse && synthesisResponse.data) {
        responseData = synthesisResponse.data
      } else {
        responseData = synthesisResponse as any
      }

      if (!responseData || !responseData.id) {
        console.error('No data or ID received from PhotoSet synthesis request')
        setIsPhotoSetGenerating(false)
        return
      }

      console.log('📋 PhotoSet synthesis response data:', responseData)

      setCurrentPhotoSetJobId(responseData.id)
    } catch (error) {
      console.error('PhotoSet generation failed:', error)
      setIsPhotoSetGenerating(false)
    }
  }, [selectedAvatar, clothingData])

  // 자동 비디오 생성 함수 (할당량 기반)
  const handleAutoVideoGeneration = useCallback(
    async (completedImages: any[]) => {
      const quotaData = videoQuotaQuery.data
      if (!quotaData) {
        console.log('비디오 할당량 정보 없음')
        return
      }

      const remainingCount = quotaData.remainingFreeVideos
      console.log('남은 비디오 할당량:', remainingCount)

      if (remainingCount <= 0) {
        console.log('비디오 할당량 부족 - 비디오 생성 스킵')
        return
      }

      try {
        // poseName이 "알 수 없음"이 아닌 이미지만 필터링 후 할당량만큼 비디오 생성
        const validImages = completedImages.filter(
          (img) => img.poseName !== '알 수 없음',
        )
        const imagesToProcess = validImages.slice(0, remainingCount)

        console.log(
          `🎬 자동 비디오 생성 시작: ${imagesToProcess.length}개 이미지 처리`,
        )

        const videoPrompt = ''
        // 각 이미지에 대해 개별 API 호출
        for (let i = 0; i < imagesToProcess.length; i++) {
          const imageUrl = imagesToProcess[i].resultUrl

          try {
            console.log(
              `🚀 비디오 요청 ${i + 1}/${imagesToProcess.length}:`,
              imageUrl,
            )

            await aiStudioService.submitVideoSynthesisRequest({
              synthesizedImageUrls: [imageUrl],
              videoPrompt,
              requestMetadata: { imageIndex: i },
            })

            console.log(
              `✅ 비디오 요청 완료 ${i + 1}/${imagesToProcess.length}`,
            )
          } catch (error) {
            console.error(`비디오 생성 실패 ${i + 1}:`, error)
          } finally {
            videoQuotaQuery.refetch()
          }
        }

        console.log('🎉 모든 비디오 요청 완료')
      } catch (error) {
        console.error('자동 비디오 생성 실패:', error)
      }
    },
    [videoQuotaQuery.data],
  )

  const handleRestartFromBeginning = useCallback(() => {
    // 모든 상태 초기화
    setCurrentStep(1)
    // 모바일에서 처음부터 다시 시작 시 최상단으로 스크롤 (지연 실행)
    if (isMobile && divRef.current) {
      setTimeout(() => {
        divRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
    setSelectedAvatar(null)
    setClothingDataState({
      category: 'top',
      topImage: null,
      bottomImage: null,
      topImageId: null,
      bottomImageId: null,
      topDescription: '',
      bottomDescription: '',
      combinedImage: [],
      generatedImages: [],
      garmentRequestId: 0,
      selectedGarmentJobId: 0,
      isUseSample: false,
      samples: [],
      sampleRequestStatus: '',
      requestStatus: '',
      aspectRatio: '9:16',
      topParsedGarmentInfo: null,
      bottomParsedGarmentInfo: null,
    })
    setPhotoSet([])
    setProcessingPhotoCount(0)
    setSelectedPhotoSetImage(null)
    setIsGenerating(false)
    setIsPhotoSetGenerating(false)
    setHasVideoRequested(false) // 비디오 요청 플래그 초기화

    // 진행 중인 폴링 중단
    if (photoSetPollingIntervalRef.current) {
      clearInterval(photoSetPollingIntervalRef.current)
    }
  }, [])

  // 컴포넌트 언마운트 시 폴링 정리
  useEffect(() => {
    return () => {
      if (photoSetPollingIntervalRef.current) {
        clearInterval(photoSetPollingIntervalRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // 스텝 변경 시 스크롤 포함하는 함수 (getSteps보다 먼저 정의)
  const goToStep = useCallback((step: number) => {
    setCurrentStep(step)
    // 모바일에서 스텝 변경 시 최상단으로 스크롤 (지연 실행)
    if (isMobile && divRef.current) {
      // setTimeout을 사용해서 상태 업데이트 후 스크롤 실행
      setTimeout(() => {
        divRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }, [])

  const nextStep = () => {
    // 총 스텝 수를 하드코딩하거나 동적으로 계산
    const totalSteps = 7 // 총 스텝 수
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
      // 모바일에서 스텝 변경 시 최상단으로 스크롤 (지연 실행)
      if (isMobile && divRef.current) {
        setTimeout(() => {
          divRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      // 모바일에서 스텝 변경 시 최상단으로 스크롤 (지연 실행)
      if (isMobile && divRef.current) {
        setTimeout(() => {
          divRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    }
  }

  const getSteps = () => {
    return [
      {
        element: (
          <ThemeSelectionStep
            selectedTheme={selectedTheme}
            setSelectedTheme={setSelectedTheme}
          />
        ),
        isNextDisabled: selectedTheme?.length !== 4,
        isPrevDeleted: true,
        onNext: () => {
          if (!userInfo) {
            navigate('/sign-up', {
              state: { from: '/ai-studio/fashion-editor' },
            })
            return
          }
          nextStep()
        },
      },
      {
        element: (
          <ModelSelectionStep
            selectedAvatar={selectedAvatar}
            setSelectedAvatar={setSelectedAvatar}
          />
        ),
        isNextDisabled: !selectedAvatar,
        isPrevDeleted: false,
      },
      {
        element: (
          <StyleConfirmStep
            selectedAvatar={selectedAvatar}
            clothingData={clothingData}
            setClothingData={setClothingData}
          />
        ),
        isNextDisabled: (() => {
          // if (!clothingData.category) return true

          // // 카테고리에 따른 필수 이미지 확인
          // switch (clothingData.category) {
          //   case 'top':
          //   case 'dress':
          //     return !clothingData.topImage
          //   case 'bottom':
          //     return !clothingData.bottomImage
          //   default:
          //     return true
          // }
          return !validateCategory()
        })(),
        isPrevDeleted: false,
      },
      {
        element: (
          <NewStyleDescriptionStep
            selectedAvatar={selectedAvatar}
            clothingData={clothingData}
            setClothingData={setClothingData}
            prevStep={prevStep}
            onNext={nextStep}
          />
        ),
        isNextDisabled: (() => {
          const {
            topImageId,
            topDescription,
            bottomImageId,
            bottomDescription,
          } = clothingData
          if (topImageId && topDescription === '') {
            return true
          } else if (bottomImageId && bottomDescription === '') {
            return true
          }
          return false
        })(),
        isPrevDeleted: true,
        isNextDeleted: true,
        onNext: () => {
          //생성된 이미지를 삭제해야지 재생성이 됌
          setClothingData((prev) => ({ ...prev, generatedImages: [] }))
          nextStep()
        },
      },
      {
        element: (
          <CompositionResultStep
            selectedTheme={selectedTheme}
            selectedAvatar={selectedAvatar}
            clothingData={clothingData}
            setClothingData={setClothingData}
            onGoToStep={goToStep}
            onGeneratingChange={setIsGenerating}
          />
        ),
        isNextDisabled:
          clothingData.sampleRequestStatus === 'failed' ||
          clothingData.generatedImages.length === 0 ||
          isGenerating,
        isPrevDeleted: false,
        onNext: async () => {
          // 실시간 포인트 체크
          const hasEnoughPoints = await checkPointsRealtime()

          if (!hasEnoughPoints) {
            // 포인트 부족 시 모달 표시
            setIsPointInsufficientModalOpen(true)
            return
          }

          // 포인트 충분하면 PhotoSet 생성 진행
          handleGeneratePhotoSet()
          // PhotoSet 생성 후 5단계로 이동
          nextStep()
        },
      },
      {
        element: (
          <PhotoSetStep
            selectedAvatar={selectedAvatar}
            clothingData={clothingData}
            photoSet={photoSet}
            isPhotoSetGenerating={isPhotoSetGenerating}
            processingCount={processingPhotoCount}
            onGeneratePhotoSet={handleGeneratePhotoSet}
            onSelectedImageChange={setSelectedPhotoSetImage}
          />
        ),
        isNextDisabled: photoSet.length === 0 || !selectedPhotoSetImage,
        isPrevDeleted: false,
        onNext: () => {
          // 6단계로 이동 (비디오는 자동 생성됨)
          nextStep()
        },
      },
      {
        element: (
          <div className="flex h-full flex-col items-center justify-center">
            <div className="text-center">
              <h2 className="mb-4 text-xl font-bold">비디오 생성 완료!</h2>
              <p className="mb-6 text-gray-600">
                할당량에 따라 비디오가 자동으로 생성되었습니다.
                <br />
                갤러리에서 확인하세요.
              </p>
              <button
                onClick={handleRestartFromBeginning}
                className="rounded-lg bg-[#00B7B9] px-6 py-2 text-white transition-colors hover:bg-[#009999]"
              >
                새로 시작하기
              </button>
            </div>
          </div>
        ),
        isNextDisabled: true, // 마지막 단계이므로 다음 버튼 비활성화
        isPrevDeleted: false, // 이전 버튼을 공통 영역에서 표시
      },
    ]
  }

  const steps = getSteps()
  const currentStepData = steps[currentStep - 1]

  const handleNext = () => {
    if (currentStepData?.onNext) {
      currentStepData.onNext()
    } else {
      nextStep()
    }
  }

  // currentPhotoSetJobId가 설정되면 React Query가 자동으로 폴링 시작
  // 수동 refetch 제거 - React Query가 즉시 시작하도록 설정됨

  useEffect(() => {
    if (data) {
      const { results } = data

      // completed 상태인 이미지만 필터링
      const completedImages = results.filter(
        (item) => item.status === 'completed',
      )

      const processingImages = results.filter(
        (item: any) => item.status === 'processing',
      )

      // processing 개수 업데이트
      setProcessingPhotoCount(processingImages.length)

      // 기존에 없는 새로 완료된 이미지만 찾기
      const newImages = completedImages.filter(
        (item) => !photoSet.find((existing) => existing.jobId === item.jobId),
      )

      // 새 이미지가 있으면 기존 배열 뒤에 추가
      if (newImages.length > 0) {
        setPhotoSet((prev) => [...prev, ...newImages])

        // 첫 번째 완료된 이미지가 있고 현재 선택된 이미지가 없다면 자동 선택
        if (photoSet.length === 0 && !selectedPhotoSetImage) {
          setSelectedPhotoSetImage(newImages[0]?.resultUrl || null)
        }

        // 할당량만큼 완료되면 한 번만 비디오 생성 요청
        const quotaData = videoQuotaQuery.data
        if (
          quotaData &&
          !hasVideoRequested &&
          completedImages.length >= quotaData.remainingFreeVideos &&
          quotaData.remainingFreeVideos > 0
        ) {
          console.log(
            `🎬 할당량 ${quotaData.remainingFreeVideos}개 달성! 비디오 생성 시작`,
          )
          // poseName이 "알 수 없음"이 아닌 이미지만 필터링 후 할당량만큼 선택
          const validImages = completedImages.filter(
            (img) => img.poseName !== '알 수 없음',
          )
          const imagesToRequest = validImages.slice(
            0,
            quotaData.remainingFreeVideos,
          )
          handleAutoVideoGeneration(imagesToRequest)
          setHasVideoRequested(true)
        }
      }

      // 현재 선택된 이미지가 유효하지 않은 경우 첫 번째 완료된 이미지로 선택
      if (
        selectedPhotoSetImage &&
        !completedImages.some((img) => img.resultUrl === selectedPhotoSetImage)
      ) {
        setSelectedPhotoSetImage(completedImages[0]?.resultUrl || null)
      }
    }
    if (data && data?.status === 'completed') {
      setIsPhotoSetGenerating(false)
      setCurrentPhotoSetJobId(null)
    } else if (data && data?.status === 'failed') {
      console.log('❌ PhotoSet generation failed:')
      setIsPhotoSetGenerating(false)
      setCurrentPhotoSetJobId(null)
    }
  }, [
    data,
    photoSet,
    selectedPhotoSetImage,
    handleAutoVideoGeneration,
    videoQuotaQuery.data,
    hasVideoRequested,
  ])

  if (isMobile) {
    // 모바일 레이아웃
    return (
      <div className="1 flex min-h-screen flex-col">
        {/* Fashion Studio 제목 (모바일 전용) */}
        <div className="relative">
          <div className="">
            <FashionStudioHero onHeightChange={setHeroHeight} />
          </div>
          {/* <div style={{ height: `${heroHeight}px` }}></div> */}
        </div>
        <div className="px-4 py-4 md:py-6" ref={divRef}>
          <div className="relative flex items-center justify-center">
            <p className="text-center font-ubuntu text-[24px] font-bold text-[#2E343D]">
              Style<span className="font-normal">Room</span>
            </p>
            <button
              onClick={() => navigate('/ai-studio/gallery')}
              className="absolute right-0 flex items-center gap-1 rounded-lg bg-[#EEF1F7] px-3 py-2 text-[12px] text-[#5E6068] transition-colors hover:bg-[#DFE3E8]"
            >
              <FolderOpen size={14} />
              생성 내역
            </button>
          </div>
        </div>

        {/* 상단 단계 표시기 */}
        <div className="px-4">
          <StepIndicator currentStep={currentStep} />
        </div>

        {/* 메인 콘텐츠 영역 */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[200px]">
          <AnimatePresence mode="wait">
            <motion.div
              className="h-full min-h-full py-4"
              key={currentStep}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              {currentStepData.element}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* 하단 고정 버튼 영역 (모바일 전용) */}
        <div
          className={classNames('', {
            '': currentStep === 4,
            'fixed bottom-0 left-0 right-0 z-50 min-h-[90px] border-t border-[#EEF1F7] bg-white px-4 pb-6 pt-2':
              currentStep !== 4,
          })}
        >
          <div className="flex w-full flex-wrap justify-center gap-2">
            {currentStep === 6 ? (
              /* 5단계일 때 3개 버튼 */
              <>
                <button
                  className={classNames(
                    'h-[54px] flex-1 rounded-[10px] text-sm font-bold',
                    {
                      'bg-[#EEF1F7] text-[#5E6068]': !isPhotoSetGenerating,
                      'cursor-not-allowed bg-[#EEF1F7] text-[#D4D9DF]':
                        isPhotoSetGenerating,
                    },
                  )}
                  type="button"
                  onClick={prevStep}
                  disabled={isPhotoSetGenerating}
                >
                  이전
                </button>
                <button
                  className={classNames(
                    'flex h-[54px] flex-[2] items-center justify-center gap-1 rounded-[10px] text-sm font-bold',
                    {
                      'cursor-not-allowed bg-main text-[#D4D9DF] opacity-30':
                        photoSet.length === 0 ||
                        !selectedPhotoSetImage ||
                        isPhotoSetGenerating ||
                        downloadProgress.isDownloading,
                      'bg-[#00B7B9] text-white':
                        !isPhotoSetGenerating &&
                        !downloadProgress.isDownloading,
                    },
                  )}
                  type="button"
                  onClick={async () => {
                    if (downloadProgress.isDownloading) return

                    const zip = new JSZip()
                    const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD 형태

                    // 진행률 초기화
                    setDownloadProgress({
                      isDownloading: true,
                      current: 0,
                      total: photoSet.length,
                      failed: 0,
                    })

                    // 병렬로 모든 이미지 다운로드
                    const downloadPromises = photoSet.map(
                      async (photo, index) => {
                        try {
                          const response = await fetch(photo.resultUrl)
                          const blob = await response.blob()
                          const fileName = `style_shot_${
                            index + 1
                          }_${today}.jpg`
                          zip.file(fileName, blob)

                          // 진행률 업데이트
                          setDownloadProgress((prev) => ({
                            ...prev,
                            current: prev.current + 1,
                          }))

                          return { success: true, index }
                        } catch (error) {
                          console.error(
                            `Failed to add image ${index + 1} to ZIP:`,
                            error,
                          )

                          // 실패 카운트 업데이트
                          setDownloadProgress((prev) => ({
                            ...prev,
                            current: prev.current + 1,
                            failed: prev.failed + 1,
                          }))

                          return { success: false, index, error }
                        }
                      },
                    )

                    // 모든 다운로드 완료 대기
                    await Promise.allSettled(downloadPromises)

                    try {
                      // ZIP 파일 생성 및 다운로드
                      const zipBlob = await zip.generateAsync({ type: 'blob' })
                      const zipUrl = window.URL.createObjectURL(zipBlob)

                      const link = document.createElement('a')
                      link.href = zipUrl
                      link.download = `style_shot_images_${today}.zip`
                      document.body.appendChild(link)
                      link.click()

                      document.body.removeChild(link)
                      window.URL.revokeObjectURL(zipUrl)
                    } catch (error) {
                      console.error('Failed to create ZIP file:', error)
                    } finally {
                      // 다운로드 완료 후 상태 초기화
                      setDownloadProgress({
                        isDownloading: false,
                        current: 0,
                        total: 0,
                        failed: 0,
                      })
                    }
                  }}
                  disabled={
                    photoSet.length === 0 ||
                    isPhotoSetGenerating ||
                    downloadProgress.isDownloading
                  }
                >
                  <Download
                    size={16}
                    className={classNames('', {
                      'text-white':
                        !isPhotoSetGenerating &&
                        !downloadProgress.isDownloading,
                      'cursor-not-allowed text-[#D4D9DF]':
                        photoSet.length === 0 ||
                        !selectedPhotoSetImage ||
                        isPhotoSetGenerating ||
                        downloadProgress.isDownloading,
                    })}
                  />
                  {downloadProgress.isDownloading
                    ? `다운로드 중... (${downloadProgress.current}/${downloadProgress.total})`
                    : '한 번에 다운로드'}
                </button>

                {(videoQuotaQuery.data?.remainingFreeVideos ?? 0) > 0 ||
                hasVideoRequested ? (
                  <button
                    className={classNames(
                      'flex h-[54px] flex-1 items-center justify-center gap-1 rounded-[10px] text-sm font-bold',
                      {
                        'bg-[#2E343D] text-white': !isPhotoSetGenerating,
                        'cursor-not-allowed bg-[#EEF1F7] text-[#8F929B]':
                          isPhotoSetGenerating,
                      },
                    )}
                    type="button"
                    onClick={() => {
                      // 조건 확인: 할당량이 있거나 비디오를 요청했을 때
                      const hasQuota =
                        (videoQuotaQuery.data?.remainingFreeVideos ?? 0) > 0
                      if (hasQuota || hasVideoRequested) {
                        setIsStyleClipPromoModalOpen(true)
                      } else {
                        // 6단계로 이동
                        nextStep()
                      }
                    }}
                    disabled={isPhotoSetGenerating}
                  >
                    <NextCameraIcon className="text-white" />
                    다음
                  </button>
                ) : (
                  /* 2줄 레이아웃: 첫 번째 줄 이전+다운로드, 두 번째 줄 처음부터 다시시작 */
                  <div className="flex w-full gap-2">
                    {/* 두 번째 줄: 처음부터 다시시작 버튼 */}

                    <button
                      className={classNames(
                        'flex h-[54px] items-center justify-center gap-1 rounded-[10px] bg-[#C4DFFF] px-2 text-sm font-bold text-[#5E6068]',
                        {
                          'cursor-not-allowed opacity-30': isPhotoSetGenerating,
                        },
                      )}
                      type="button"
                      onClick={() => {
                        navigate('/ai-studio/gallery')
                      }}
                      disabled={isPhotoSetGenerating}
                    >
                      생성 내역 보러가기
                    </button>

                    <button
                      className={classNames(
                        'flex h-[54px] flex-1 items-center justify-center gap-1 rounded-[10px] bg-[#2E343D] text-sm font-bold text-white',
                        {
                          'cursor-not-allowed opacity-30': isPhotoSetGenerating,
                        },
                      )}
                      type="button"
                      onClick={handleRestartFromBeginning}
                      disabled={isPhotoSetGenerating}
                    >
                      <RotateCcw size={16} className="text-white" />
                      처음부터 다시시작
                    </button>
                  </div>
                )}
              </>
            ) : currentStep === 7 ? (
              /* 6단계일 때 2개 버튼 */
              <>
                <button
                  className="h-[54px] flex-1 rounded-[10px] bg-[#EEF1F7] text-sm font-bold text-[#5E6068]"
                  type="button"
                  onClick={prevStep}
                >
                  이전
                </button>
                <button
                  className="flex h-[54px] flex-1 items-center justify-center gap-1 rounded-[10px] bg-[#EEF1F7] text-sm font-bold text-[#5E6068]"
                  type="button"
                  onClick={handleRestartFromBeginning}
                >
                  <RotateCcw size={16} className="text-[#5E6068]" />
                  처음부터 다시시작
                </button>
              </>
            ) : currentStep === 5 ? (
              /* 4단계일 때 Figma 디자인에 맞는 버튼 레이아웃 */
              <>
                <button
                  className={classNames(
                    'h-[50px] w-[90px] rounded-[10px] bg-[#EEF1F7] text-sm font-bold text-[#5E6068]',
                    {
                      'cursor-not-allowed bg-[#EEF1F7] text-[#D4D9DF]':
                        isGenerating,
                    },
                  )}
                  type="button"
                  onClick={prevStep}
                  disabled={isGenerating}
                >
                  이전
                </button>
                <button
                  className={classNames(
                    'flex h-[50px] flex-1 items-center justify-center gap-4 rounded-[10px] bg-[#2E343D] text-sm font-bold text-white transition-colors',
                    {
                      // 4단계일 때 특별한 디자인
                      'gap-4 bg-[#2E343D] px-2 text-white': currentStep === 5,
                      // 일반 버튼 디자인
                      'w-[160px] bg-[#00B7B9] px-6 text-white':
                        currentStep !== 5,
                      // 비활성화 상태
                      'disabled:bg-[#2E343D] disabled:text-white disabled:opacity-30':
                        currentStep === 5,
                      'disabled:bg-main disabled:text-white disabled:opacity-30':
                        currentStep !== 5,
                    },
                  )}
                  type="button"
                  onClick={handleNext}
                  disabled={currentStepData.isNextDisabled}
                >
                  {/* 왼쪽 아이콘 그룹 */}
                  <div className="flex items-center gap-1">
                    <StyleShotIcon className="text-white" />
                    <span>스타일 샷 세트 생성</span>
                  </div>

                  {/* 오른쪽 캐시 표시 */}
                  <div className="flex items-center gap-0.5">
                    <div className="flex items-center">
                      <img
                        src="assets/img/cash.png"
                        alt="cash"
                        className="mr-0.5 h-[13px] w-[11px]"
                      />
                      <s className="mr-1 text-sm">9,000</s>
                      <span className="text-sm">900</span>
                    </div>
                  </div>
                </button>
              </>
            ) : currentStep === 1 ? (
              <>
                <button
                  className={classNames(
                    'flex h-[54px] w-full items-center justify-center rounded-[10px] bg-main font-bold text-white transition-colors',
                    {
                      'cursor-not-allowed disabled:opacity-30':
                        selectedTheme.length === 0,
                    },
                  )}
                  type="button"
                  onClick={handleNext}
                  disabled={selectedTheme.length === 0}
                >
                  다음
                </button>
              </>
            ) : (
              /* 일반 단계 버튼 레이아웃 */
              <>
                {!currentStepData?.isPrevDeleted && (
                  <button
                    className={classNames(
                      'h-[54px] flex-1 rounded-[10px] bg-[#EEF1F7] text-sm font-bold text-[#5E6068]',
                      {
                        'cursor-not-allowed bg-[#EEF1F7] text-[#D4D9DF]':
                          currentStep === 1,
                      },
                    )}
                    type="button"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                  >
                    이전
                  </button>
                )}

                {!currentStepData?.isNextDeleted && (
                  <button
                    className={classNames(
                      'flex h-[54px] flex-1 items-center justify-center gap-1 rounded-[10px] bg-[#00B7B9] text-sm font-bold text-white transition-colors',
                      {
                        'opacity-30 disabled:bg-main disabled:text-white':
                          currentStepData.isNextDisabled,
                      },
                    )}
                    type="button"
                    onClick={handleNext}
                    disabled={currentStepData.isNextDisabled}
                  >
                    다음
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* 스타일 클립 프로모 모달 */}
        <StyleClipPromoModal
          isOpen={isStyleClipPromoModalOpen}
          onClose={() => {
            setIsStyleClipPromoModalOpen(false)
            // 갤러리 이동은 모달 내부에서 처리하므로 nextStep() 제거
          }}
        />

        {/* 포인트 부족 모달 */}
        <PointInsufficientModal
          isOpen={isPointInsufficientModalOpen}
          onClose={() => setIsPointInsufficientModalOpen(false)}
          currentPoint={currentPoint}
          requiredPoint={900}
        />
      </div>
    )
  }

  // 데스크톱 레이아웃
  return (
    <div className="flex h-full flex-col items-center gap-6">
      {/* 안내 섹션 */}
      <div className="flex flex-col items-center gap-10">
        <div className="relative">
          <div className="w-screen transform">
            <FashionStudioHero onHeightChange={setHeroHeight} />
          </div>
        </div>
        <div className="relative flex w-[816px] items-center justify-center">
          <p className="text-center font-ubuntu text-[32px] font-bold text-[#2E343D]">
            Style<span className="font-normal">Room</span>
          </p>
          <button
            onClick={() => navigate('/ai-studio/gallery')}
            className="absolute right-0 flex items-center gap-2 rounded-lg bg-[#EEF1F7] px-4 py-2 text-[#5E6068] transition-colors hover:bg-[#DFE3E8]"
          >
            <FolderOpen size={20} />
            생성 내역
          </button>
        </div>
      </div>

      {/* 스튜디오 tab */}
      <div className="relative flex h-[660px] w-[816px] gap-6 rounded-[20px] border border-[#EEF1F7] bg-white px-6 py-10 shadow-main">
        {/* 좌측 단계 표시기 */}
        <StepIndicator currentStep={currentStep} />

        {/* 우측 메인 콘텐츠 */}
        <div className="flex flex-1 flex-col justify-between">
          {/* 실제 콘텐츠 영역 */}
          <div className="relative flex-1">
            <AnimatePresence mode="wait">
              <motion.div
                className="h-full"
                key={currentStep}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                {currentStepData.element}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* 하단 버튼 영역 */}
          <div className="mt-2 flex justify-end gap-2">
            {currentStep === 6 ? (
              /* 5단계일 때 2개 버튼 */
              <>
                <button
                  className={classNames(
                    'h-[54px] w-full flex-1 rounded-[10px] font-bold',
                    {
                      'bg-[#EEF1F7] text-[#5E6068]': !isPhotoSetGenerating,
                      'cursor-not-allowed bg-[#EEF1F7] text-[#D4D9DF]':
                        isPhotoSetGenerating,
                    },
                  )}
                  type="button"
                  onClick={prevStep}
                  disabled={isPhotoSetGenerating}
                >
                  이전
                </button>

                <button
                  className={classNames(
                    'flex h-[54px] w-[160px] items-center justify-center gap-1 rounded-[10px] bg-[#C4DFFF] font-bold text-[#5E6068]',
                    {
                      'cursor-not-allowed opacity-30': isPhotoSetGenerating,
                    },
                  )}
                  type="button"
                  onClick={() => {
                    navigate('/ai-studio/gallery')
                  }}
                  disabled={isPhotoSetGenerating}
                >
                  생성 내역 보러가기
                </button>
                <button
                  className={classNames(
                    'flex h-[54px] min-w-[160px] items-center justify-center gap-1 rounded-[10px] bg-[#00B7B9] px-4 font-bold text-white',
                    {
                      'disabled:opacity-30':
                        photoSet.length === 0 ||
                        isPhotoSetGenerating ||
                        downloadProgress.isDownloading,
                    },
                  )}
                  type="button"
                  onClick={async () => {
                    if (downloadProgress.isDownloading) return

                    const zip = new JSZip()
                    const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD 형태

                    // 진행률 초기화
                    setDownloadProgress({
                      isDownloading: true,
                      current: 0,
                      total: photoSet.length,
                      failed: 0,
                    })

                    // 병렬로 모든 이미지 다운로드
                    const downloadPromises = photoSet.map(
                      async (photo, index) => {
                        try {
                          const response = await fetch(photo.resultUrl)
                          const blob = await response.blob()
                          const fileName = `style_shot_${
                            index + 1
                          }_${today}.jpg`
                          zip.file(fileName, blob)

                          // 진행률 업데이트
                          setDownloadProgress((prev) => ({
                            ...prev,
                            current: prev.current + 1,
                          }))

                          return { success: true, index }
                        } catch (error) {
                          console.error(
                            `Failed to add image ${index + 1} to ZIP:`,
                            error,
                          )

                          // 실패 카운트 업데이트
                          setDownloadProgress((prev) => ({
                            ...prev,
                            current: prev.current + 1,
                            failed: prev.failed + 1,
                          }))

                          return { success: false, index, error }
                        }
                      },
                    )

                    // 모든 다운로드 완료 대기
                    await Promise.allSettled(downloadPromises)

                    try {
                      // ZIP 파일 생성 및 다운로드
                      const zipBlob = await zip.generateAsync({ type: 'blob' })
                      const zipUrl = window.URL.createObjectURL(zipBlob)

                      const link = document.createElement('a')
                      link.href = zipUrl
                      link.download = `style_shot_images_${today}.zip`
                      document.body.appendChild(link)
                      link.click()

                      document.body.removeChild(link)
                      window.URL.revokeObjectURL(zipUrl)
                    } catch (error) {
                      console.error('Failed to create ZIP file:', error)
                    } finally {
                      // 다운로드 완료 후 상태 초기화
                      setDownloadProgress({
                        isDownloading: false,
                        current: 0,
                        total: 0,
                        failed: 0,
                      })
                    }
                  }}
                  disabled={
                    photoSet.length === 0 ||
                    isPhotoSetGenerating ||
                    downloadProgress.isDownloading
                  }
                >
                  <Download size={16} className="text-white" />
                  {downloadProgress.isDownloading
                    ? `다운로드 중... (${downloadProgress.current}/${downloadProgress.total})`
                    : '한 번에 다운로드'}
                </button>
                {(videoQuotaQuery.data?.remainingFreeVideos ?? 0) > 0 ||
                hasVideoRequested ? (
                  <button
                    className={classNames(
                      'flex h-[54px] w-[160px] items-center justify-center gap-1 rounded-[10px] bg-[#2E343D] font-bold text-white',
                      {
                        'cursor-not-allowed opacity-30': isPhotoSetGenerating,
                      },
                    )}
                    type="button"
                    onClick={() => {
                      // 조건 확인: 할당량이 있거나 비디오를 요청했을 때
                      const hasQuota =
                        (videoQuotaQuery.data?.remainingFreeVideos ?? 0) > 0
                      if (hasQuota || hasVideoRequested) {
                        setIsStyleClipPromoModalOpen(true)
                      } else {
                        // 6단계로 이동
                        nextStep()
                      }
                    }}
                    disabled={isPhotoSetGenerating}
                  >
                    <NextCameraIcon className="text-white" />
                    다음
                  </button>
                ) : (
                  <button
                    className={classNames(
                      'flex h-[54px] w-[160px] items-center justify-center gap-1 rounded-[10px] bg-[#2E343D] font-bold text-white',
                      {
                        'cursor-not-allowed opacity-30': isPhotoSetGenerating,
                      },
                    )}
                    type="button"
                    onClick={handleRestartFromBeginning}
                    disabled={isPhotoSetGenerating}
                  >
                    <RotateCcw size={16} className="text-white" />
                    처음부터 다시시작
                  </button>
                )}
              </>
            ) : currentStep === 7 ? (
              /* 6단계일 때 2개 버튼 */
              <>
                <button
                  className="h-[54px] w-[160px] rounded-[10px] bg-[#EEF1F7] font-bold text-[#5E6068]"
                  type="button"
                  onClick={prevStep}
                >
                  이전
                </button>
                <button
                  className="flex h-[54px] w-[160px] items-center justify-center gap-1 rounded-[10px] bg-[#EEF1F7] font-bold text-[#5E6068]"
                  type="button"
                  onClick={handleRestartFromBeginning}
                >
                  <RotateCcw size={16} className="text-[#5E6068]" />
                  처음부터 다시시작
                </button>
              </>
            ) : currentStep === 1 ? (
              <>
                <button
                  className={classNames(
                    'flex h-[54px] w-[160px] items-center justify-center rounded-[10px] bg-main font-bold text-white transition-colors',
                    {
                      'cursor-not-allowed disabled:opacity-30':
                        selectedTheme.length === 0,
                    },
                  )}
                  type="button"
                  onClick={handleNext}
                  disabled={selectedTheme.length === 0}
                >
                  다음
                </button>
              </>
            ) : (
              /* 다른 단계일 때 기본 버튼 레이아웃 */
              <>
                {!currentStepData?.isPrevDeleted && (
                  <button
                    className={classNames(
                      'h-[54px] w-[160px] rounded-[10px] font-bold',
                      {
                        'bg-[#EEF1F7] text-[#5E6068]': !(
                          currentStep === 1 ||
                          (currentStep === 5 && isGenerating)
                        ),
                        'cursor-not-allowed bg-[#EEF1F7] text-[#D4D9DF]':
                          currentStep === 1 ||
                          (currentStep === 5 && isGenerating),
                      },
                    )}
                    type="button"
                    onClick={prevStep}
                    disabled={
                      currentStep === 1 || (currentStep === 5 && isGenerating)
                    }
                  >
                    이전
                  </button>
                )}
                {!currentStepData?.isNextDeleted && (
                  <button
                    className={classNames(
                      'flex h-[54px] items-center justify-center rounded-[10px] font-bold transition-colors',
                      {
                        // 4단계일 때 특별한 디자인
                        'gap-4 bg-[#2E343D] px-6 text-white': currentStep === 5,
                        // 일반 버튼 디자인
                        'w-[160px] bg-[#00B7B9] text-white': currentStep !== 5,
                        // 비활성화 상태
                        'disabled:bg-[#2E343D] disabled:text-white disabled:opacity-30':
                          currentStep === 5,
                        'disabled:bg-main disabled:text-white disabled:opacity-30':
                          currentStep !== 5,
                      },
                    )}
                    type="button"
                    onClick={handleNext}
                    disabled={currentStepData.isNextDisabled}
                  >
                    {currentStep === 5 ? (
                      <>
                        {/* 왼쪽 아이콘 그룹 */}
                        <div className="flex items-center gap-1">
                          {/* 카메라 아이콘 */}
                          <StyleShotIcon className="text-white" />

                          <span>스타일 샷 세트 생성</span>
                        </div>

                        {/* 오른쪽 캐시 표시 */}
                        <div className="flex items-center gap-0.5">
                          {/* 캐시 이미지와 숫자 */}
                          <div className="flex items-center">
                            <img
                              src="assets/img/cash.png"
                              alt="cash"
                              className="mr-0.5 h-[13px] w-[11px]"
                            />
                            <s className="mr-1 text-sm">9,000</s>
                            <span className="text-sm">900</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center gap-1">다음</div>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <p className="text-center">
        패스트컷 AI 스타일룸 사용자가 제공하는 이미지를 토대로 이미지와 영상을
        생성합니다.
        <br /> 패스트컷의 AI는 학습중이며, AI 특성상 실수를 할 수 있습니다.
      </p>

      {/* 스타일 클립 프로모 모달 */}
      <StyleClipPromoModal
        isOpen={isStyleClipPromoModalOpen}
        onClose={() => {
          setIsStyleClipPromoModalOpen(false)
          // 갤러리 이동은 모달 내부에서 처리하므로 nextStep() 제거
        }}
      />

      {/* 포인트 부족 모달 */}
      <PointInsufficientModal
        isOpen={isPointInsufficientModalOpen}
        onClose={() => setIsPointInsufficientModalOpen(false)}
        currentPoint={currentPoint}
        requiredPoint={900}
      />
    </div>
  )
}
